// Root layout (B5B §3.1): SessionProvider → QueryClientProvider → the shadcn admin shell + <Outlet>.
// PLAN-C4.0 task-2. The flat inline-styled link row was replaced by the shadcn AppShell — the same
// VISUAL treatment as the tenant app (Aquino Compliance wordmark, top app bar, nav tab strip), with
// an "Admin" badge and the operator's own nav items (Dashboard, Canonical libraries, Consultancy
// firms, Billing, Counsel review, Compliance, Eval runs). Permission gating via <Can> is unchanged.
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { QueryClient, QueryCache, MutationCache, QueryClientProvider } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { handleSessionExpired } from "../lib/auth";
import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard, Library, Building2, CreditCard, Gavel, ShieldCheck, FlaskConical, LogOut,
} from "lucide-react";
import { hasPermission, type PermissionCode } from "@sympllex/auth";
import { SessionProvider, useSession } from "../lib/session";
import { AUTH_BASE } from "../lib/env";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// A 401 that survives the api-client's silent refresh-and-retry means the session is genuinely over →
// re-auth through AuthKit. Don't retry 4xx; retry transient network/5xx once.
const onAuthError = (err: unknown) => { if (err instanceof ApiError && err.status === 401) handleSessionExpired(); };
const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && failureCount < 1,
    },
  },
});

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** permission code gating the link (omit = always visible). */ perm?: PermissionCode;
}

// Admin information architecture — the super-tenant operator surfaces. Single source of truth for
// the shell nav. Permission codes match the previous inline-styled <Can> gates exactly.
const NAV: NavItem[] = [
  { label: "Dashboard", to: "/", icon: LayoutDashboard },
  { label: "Canonical libraries", to: "/canonical-libraries", icon: Library, perm: "super_tenant.canonical_library.write" },
  { label: "Consultancy firms", to: "/consultancy-firms", icon: Building2, perm: "super_tenant.canonical_library.write" },
  { label: "Billing", to: "/billing", icon: CreditCard, perm: "tenant_billing.read" },
  { label: "Counsel review", to: "/counsel", icon: Gavel, perm: "super_tenant.security_audit.read" },
  { label: "Compliance", to: "/compliance", icon: ShieldCheck, perm: "super_tenant.security_audit.read" },
  { label: "Eval runs", to: "/eval-runs", icon: FlaskConical, perm: "super_tenant.security_audit.read" },
];

function AppShell() {
  const session = useSession();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const can = (perm?: PermissionCode) => !perm || hasPermission(session, perm);
  const logout = async () => {
    await fetch(`${AUTH_BASE}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    window.location.href = `${AUTH_BASE}/auth/login`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top app bar — tenant visual treatment + an Admin badge. */}
      <header className="border-b bg-card">
        <div className="flex h-14 items-center gap-3 px-5">
          <Link to="/" className="shrink-0 whitespace-nowrap text-[15px] font-semibold tracking-tight text-primary">Aquino Compliance</Link>
          <Badge variant="secondary" className="shrink-0 uppercase tracking-wide">Admin</Badge>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden text-sm text-muted-foreground sm:inline">{session.user.email}</span>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={logout}>
              <LogOut className="size-4" />
              <span className="sr-only sm:not-sr-only">Sign out</span>
            </Button>
          </div>
        </div>
        {/* Nav tab strip — same treatment as the tenant persona tabs. */}
        <nav className="flex flex-wrap gap-1 px-3" aria-label="Admin sections">
          {NAV.filter((i) => can(i.perm)).map((i) => {
            const Icon = i.icon;
            const isActive = i.to === "/" ? path === "/" : path.startsWith(i.to);
            return (
              <Link key={i.to} to={i.to}
                className={cn("flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm",
                  isActive ? "border-primary font-medium text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>
                <Icon className="size-4" />{i.label}
              </Link>
            );
          })}
        </nav>
      </header>

      <main className="mx-auto min-w-0 max-w-6xl px-6 py-6"><Outlet /></main>
    </div>
  );
}

export function RootLayout() {
  return (
    <SessionProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={300}>
          <AppShell />
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </SessionProvider>
  );
}
